import { TypedDataEncoder, TypedDataDomain, TypedDataField } from "ethers";
import { CreateOrder, OrderMethodPayload, OrderStateKey, OrderType } from "@sdk/shared/types/order.types";
import { WalletClientSigner } from "@aa-sdk/core";
import { AlchemySigner } from "@account-kit/core";
import { useHostStore } from "../stores/useHostStore";
import { SdkClient } from "@sdk/index";
import { IS_SPONSORSHIP_ENABLED } from "@sdk/shared/constants";

export function getExpiryTime() {
  const currentTime = Date.now(); // Current time in seconds
  const expiryTime = currentTime + 60 * 15; // Add 15 minutes (in seconds)
  return expiryTime;
}

// https://docs.ethers.org/v6/api/hashing/#TypedDataEncoder
export function encodedCreateOrder(): TypedDataEncoder {
  return new TypedDataEncoder({
    CreateOrder: [
      {
        name: "trader",
        type: "address",
      },
      {
        name: "merchant",
        type: "address",
      },
      {
        name: "traderChain",
        type: "uint256",
      },
      {
        name: "merchantChain",
        type: "uint256",
      },
      {
        name: "token",
        type: "address",
      },
      {
        name: "currency",
        type: "bytes32",
      },
      {
        name: "paymentMethod",
        type: "bytes32",
      },
      {
        name: "orderType",
        type: "uint8",
      },
      {
        name: "quantity",
        type: "uint256",
      },
      {
        name: "expiry",
        type: "uint256",
      },
      {
        name: "duration",
        type: "uint256",
      },
    ],
  });
}

export function getStatusForState(state: OrderStateKey) {
  switch (state) {
    case "pending":
      return "Pending payment";
    case "accepted":
      return "Accepted";
    case "appealed":
      return "Appealed";
    case "cancelled":
      return "cancelled";
    case "paid":
      return "Marked Paid";
    case "released":
      return "Released";
    case "intent":
      return "Pending Acceptance";
  }
}

export async function signTypedData(
  signer: WalletClientSigner | AlchemySigner,
  domain: TypedDataDomain,
  types: Record<string, TypedDataField[]>,
  message: Record<string, any>,
  primaryType: `0x${string}`,
): Promise<`0x${string}`> {
  let sig: `0x${string}`;
  if (signer instanceof WalletClientSigner) {
    sig = await signer.signTypedData({ domain: domain as any, primaryType: primaryType, types, message });
  } else {
    sig = await signer.signTypedData({ domain: domain as any, primaryType: primaryType, types, message });
  }
  return sig;
}

// https://docs.ethers.org/v6/api/hashing/#TypedDataEncoder
export function encodedOrderMethodPayload(): TypedDataEncoder {
  return new TypedDataEncoder({
    OrderMethodPayload: [
      {
        name: "orderHash",
        type: "bytes32",
      },
      {
        name: "method",
        type: "uint8",
      },
      {
        name: "expiry",
        type: "uint256",
      },
    ],
  });
}

export function createOrderMethodTypedDataHash(
  orderMethod: OrderMethodPayload,
  domain: TypedDataDomain,
): `0x${string}` {
  return TypedDataEncoder.hash(domain, encodedOrderMethodPayload().types, orderMethod) as `0x${string}`;
}

export function checkIfGasSponsored({
  chainId,
  destinationAddress,
  functionName,
  sdk,
  connectorId,
}: {
  chainId: number;
  destinationAddress: string;
  functionName?: string;
  sdk: SdkClient;
  connectorId?: "baseAccount" | "coinbaseWallet" | "other" | string;
}): boolean {
  if (!IS_SPONSORSHIP_ENABLED) return false;
  if (connectorId === "baseAccount") return false;
  if (!sdk?.config) throw new Error("SDK not found, checking sposorship");
  const p2pContract = sdk.config.networks?.find(n => n.id === chainId)?.contract.toLowerCase();
  const tokenAddresses = sdk.config.tokens?.map(t => t.address.toLowerCase()) ?? [];

  if (!sdk.environment.SPONSORED_CHAINS?.find(c => c.id === chainId)) {
    return false;
  }
  if (destinationAddress.toLowerCase() === p2pContract) {
    return true;
  }
  if (!(tokenAddresses.includes(destinationAddress.toLowerCase()) && functionName === "permit")) {
    return false;
  }
  return true;
}

interface Order {
  trader: `0x${string}`;
  merchant: `0x${string}`;
  token: `0x${string}`;
  currency: `0x${string}`;
  paymentMethod: `0x${string}`;
  tokenDecimals: number;
  quantity: number;
  orderType: OrderType;
  traderChain: number;
  merchantChain: number;
}

export function sameChainOrder({
  trader,
  merchant,
  token,
  currency,
  paymentMethod,
  tokenDecimals,
  quantity,
  orderType,
  traderChain,
  merchantChain,
}: Order): CreateOrder {
  const expiry = Math.floor(Date.now() / 1000) + 60 * 15;

  return {
    trader,
    merchant,
    traderChain: BigInt(traderChain),
    merchantChain: BigInt(merchantChain),
    token,
    currency,
    paymentMethod,
    orderType,
    quantity: BigInt(Math.floor(quantity * 10 ** tokenDecimals)),
    expiry: BigInt(expiry),
    duration: BigInt(1800),
  };
}
